import { Link } from 'react-router-dom';
import { 
  Users, CalendarCheck, Wallet, BookOpen, MessageSquare, BarChart3, 
  ShieldCheck, Smartphone, Library, Globe, Building, Settings, 
  CheckCircle2, Sparkles, ArrowRight 
} from 'lucide-react';

export default function Features() {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-wrapper" style={{ paddingBottom: '4rem' }}>
        <div className="grid-2">
          <div>
            <span className="section-label">
              <Sparkles size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Comprehensive Features
            </span>
            <h1 className="hero-gradient-text" style={{ fontSize: '3rem' }}>
              Everything You Need to Run a Modern School
            </h1>
            <p className="section-desc" style={{ fontSize: '1.2rem', marginBottom: '2.5rem' }}>
              Powerful, intuitive features designed to simplify school management, improve communication, and deliver real-time insights.
            </p>
            <div>
              <button 
                onClick={() => document.getElementById('core-features')?.scrollIntoView({ behavior: 'smooth' })} 
                className="btn btn-primary btn-lg"
              >
                Explore the Features
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="hero-mockup-wrapper" style={{ padding: '2rem' }}>
            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center', margin: 0 }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0, background: 'var(--primary-glow)', color: 'var(--primary)' }}>
                <CheckCircle2 size={32} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>Unified Platform</h3>
                <p style={{ margin: 0, fontSize: '0.95rem' }}>One centralized cloud platform connecting students, teachers, parents, and administrators seamlessly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Solutions Section */}
      <section id="core-features" style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', background: 'rgba(241, 245, 249, 0.2)' }}>
        <div className="section-wrapper">
          <div className="text-center-wrapper">
            <span className="section-label">Core Solutions</span>
            <h2 className="section-title">Built to streamline every school operation</h2>
          </div>

          <div className="grid-3">
            <div className="premium-card">
              <div className="icon-box">
                <Users size={24} />
              </div>
              <h3>Student Management</h3>
              <p>Complete student lifecycle from admission to alumni with detailed profiles, academic records, and progress tracking.</p>
            </div>

            <div className="premium-card">
              <div className="icon-box">
                <CalendarCheck size={24} />
              </div>
              <h3>Smart Attendance</h3>
              <p>Automated attendance tracking with biometric integration, real-time notifications, and comprehensive analytics.</p>
            </div>

            <div className="premium-card">
              <div className="icon-box">
                <Wallet size={24} />
              </div>
              <h3>Fee Management</h3>
              <p>Streamlined fee collection with online payments, automated reminders, and detailed financial reporting.</p>
            </div>

            <div className="premium-card">
              <div className="icon-box">
                <BookOpen size={24} />
              </div>
              <h3>Exam Management</h3>
              <p>Plan exams, manage marks, and generate report cards with powerful grading workflows.</p>
            </div>

            <div className="premium-card">
              <div className="icon-box">
                <MessageSquare size={24} />
              </div>
              <h3>Communication Hub</h3>
              <p>Instant messaging, announcements, and notifications keep teachers, parents, and students aligned.</p>
            </div>

            <div className="premium-card">
              <div className="icon-box">
                <BarChart3 size={24} />
              </div>
              <h3>Analytics & Reports</h3>
              <p>Actionable insights from attendance, fees, results, and school performance dashboards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="section-wrapper">
        <div className="text-center-wrapper">
          <span className="section-label">More Capabilities</span>
          <h2 className="section-title">Advanced tools for complete management</h2>
        </div>

        <div className="grid-3">
          <div className="premium-card">
            <div className="icon-box">
              <ShieldCheck size={24} />
            </div>
            <h3>Multiple Roles & Permissions</h3>
            <p>Granular access control for administrators, teachers, staff, students, and parents.</p>
          </div>

          <div className="premium-card">
            <div className="icon-box">
              <Smartphone size={24} />
            </div>
            <h3>Android & iOS Apps</h3>
            <p>Native mobile apps ensure seamless access on the go for every member of your school community.</p>
          </div>

          <div className="premium-card">
            <div className="icon-box">
              <Library size={24} />
            </div>
            <h3>Library & Inventory</h3>
            <p>Track library circulation, books, and school inventory from one system.</p>
          </div>

          <div className="premium-card">
            <div className="icon-box">
              <Globe size={24} />
            </div>
            <h3>Built-in Website</h3>
            <p>Launch a professional school website with customizable templates and news updates.</p>
          </div>

          <div className="premium-card">
            <div className="icon-box">
              <Building size={24} />
            </div>
            <h3>Multi-Branch Support</h3>
            <p>Manage multiple branches from a centralized platform with unified reporting.</p>
          </div>

          <div className="premium-card">
            <div className="icon-box">
              <Settings size={24} />
            </div>
            <h3>Flexible Configuration</h3>
            <p>Highly customizable workflows, fields, and reports that adapt to your school's needs.</p>
          </div>
        </div>
      </section>

      {/* CTA Footer Section */}
      <section style={{ background: 'var(--gradient-primary)', color: '#ffffff', borderTop: '1px solid var(--border-color)' }}>
        <div className="section-wrapper" style={{ textAlign: 'center', padding: '6rem 1.5rem' }}>
          <span className="section-label" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', color: '#ffffff' }}>
            Ready to Get Started?
          </span>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#ffffff', marginBottom: '1.5rem', fontFamily: 'var(--font-heading)' }}>
            Experience the power of Roriri with a 30-day free trial
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 2.5rem auto' }}>
            No credit card required. Full onboarding support to guide you through setup and data migration.
          </p>
          <Link to="/pricing" className="btn btn-outline btn-lg" style={{ background: '#ffffff', color: 'var(--primary)', border: 'none' }}>
            View Pricing Plans
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
